import { useState, useEffect } from 'react';

import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Card,
  Table,
  Button,
  Divider,
  TableRow,
  useTheme,
  Accordion,
  TableBody,
  TableCell,
  TextField,
  Pagination,
  Typography,
  CardActions,
  InputAdornment,
  TableContainer,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material';

import useLocales from 'src/hooks/useLocales';
import useResponsive from 'src/hooks/useResponsive';

import { fDateTime } from 'src/utils/formatTime';
import eventBus, { EventTypes } from 'src/utils/eventBus';

import { useSelector } from 'src/redux/store';
import { getHistory, downloadHistory } from 'src/redux/slices/documentHistory';
import { DocumentHistoryItem } from 'src/@types/documents/documentHistoryItem';
import { DocumentHistoryFilter } from 'src/@types/documents/documentHistoryFilter';
import { DocumentActionTypes } from 'src/@types/documents/enums/documentActionTypes';

import Tip from 'src/components/Tip';
import Iconify from 'src/components/Iconify';
import Scrollbar from 'src/components/Scrollbar';

// ----------------------------------------------------------------------

export default function DocumentHistory() {
  const { t } = useLocales();
  const { selectedId, document } = useSelector((state) => state.document);
  const { history, isLoading } = useSelector((state) => state.documentHistory);
  const isMobile = useResponsive('down', 'sm');
  const pageSize = 5;
  const [page, setPage] = useState(1);
  const [eventState, setEventState] = useState({});
  const theme = useTheme();
  const [searchButtonPressed, setSearchButtonPressed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const updateHistory = (payload: any) => {
      if (page !== 1) {
        setPage(1);
      } else {
        setEventState(payload);
      }
    };

    const documentSignedSubscription = eventBus.subscribe(EventTypes.DocumentSigned, updateHistory);
    const signersChangedSubscription = eventBus.subscribe(EventTypes.SignersChanged, updateHistory);
    const repeatInviteSubscription = eventBus.subscribe(EventTypes.RepeatedInvite, updateHistory);

    return () => {
      documentSignedSubscription.unsubscribe();
      signersChangedSubscription.unsubscribe();
      repeatInviteSubscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const filter = new DocumentHistoryFilter();
    filter.limit = pageSize;
    filter.offset = page - 1;
    filter.id = selectedId;
    filter.searchQuery = searchQuery;
    getHistory(filter);
  }, [selectedId, page, eventState, searchQuery, document]);

  function pageCount() {
    return Math.ceil((history?.total || 0) / pageSize);
  }

  const handleChange = (event: any, value: number) => {
    setPage(value);
  };

  function documentHistoryDescription(item: DocumentHistoryItem) {
    if (item.customData) {
      return item.customData;
    }

    if (!item.customData && item.action === DocumentActionTypes.DocumentHistoryExport) {
      return '';
    }

    return t('documents.document').toLowerCase();
  }

  const tableStyle = {
    padding: 0,
    '& > .MuiTableBody-root > .MuiTableRow-root > .MuiTableCell-root': {
      '&:first-of-type': {
        paddingLeft: 0,
      },
      '&:last-of-type': {
        paddingRight: 0,
      },
    },
  };

  const handleSearchQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    // handleColumnFiltersChange({ NameFilter: { query: event.target.value } });
  };

  // eslint-disable-next-line react/no-unstable-nested-components
  const SearchControl = ({ fullWidth }: any) => (
    <>
      {!searchButtonPressed && !isMobile && (
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => setSearchButtonPressed(true)}
          id="90ojdj0y"
        >
          <SearchIcon />
        </Button>
      )}
      {(searchButtonPressed || isMobile) && (
        <TextField
          autoFocus
          fullWidth={fullWidth}
          size="small"
          label={t('shared.fields.searchBy')}
          value={searchQuery}
          onChange={handleSearchQueryChange}
          onBlur={() => !searchQuery.length && setSearchButtonPressed(false)}
          id="3rnmcwgi"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />
      )}
    </>
  );

  const onDownloadHistory = async () => {
    await downloadHistory(selectedId);
  };

  return (
    <Card data-testid="document-history">
      {history && (
        <Accordion defaultExpanded={!isMobile} sx={{ p: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 3 }}>
            <Typography variant="h6"> {t('documents.history')}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3, pt: 0 }}>
            <Box>{SearchControl({ fullWidth: true })}</Box>

            <Scrollbar>
              <TableContainer>
                <Table sx={tableStyle}>
                  <TableBody>
                    {history.items.map((item, index) => (
                      <TableRow key={index}>
                        {!isMobile && (
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontSize: '1rem',
                                color: theme.palette.grey[700],
                                fontWeight: 400,
                              }}
                            >
                              {fDateTime(item.dateTime)}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell sx={isMobile ? { px: 0, py: 0.5 } : {}}>
                          <Box>
                            {isMobile && (
                              <Typography variant="caption" sx={{ color: theme.palette.grey[500] }}>
                                {fDateTime(item.dateTime)}
                              </Typography>
                            )}
                            <Typography
                              variant={isMobile ? 'body2' : 'body1'}
                              sx={{ color: theme.palette.grey[600] }}
                            >
                              {item.executorName}
                            </Typography>

                            <Typography
                              variant={isMobile ? 'body2' : 'body1'}
                              sx={{ color: theme.palette.grey[700] }}
                            >
                              <b style={{ fontWeight: 600 }}>
                                {t(`documents.actionNames.${item.actionDisplayLocaleTag}`)}
                              </b>
                              &nbsp;
                              {documentHistoryDescription(item)}
                            </Typography>
                            {item.action === DocumentActionTypes.Rejected &&
                              item.rejectMessage !== '' && (
                                <Typography
                                  variant={isMobile ? 'body2' : 'body1'}
                                  sx={{ color: theme.palette.grey[700] }}
                                >
                                  {item.rejectMessage}
                                </Typography>
                              )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
            <Divider />

            <CardActions
              sx={{
                py: isMobile ? 1.5 : 3,
                pb: 0,
                px: 0,
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Tip titleLocale="documents.buttons.downloadHistory">
                <Button
                  onClick={() => onDownloadHistory()}
                  color="secondary"
                  size="medium"
                  variant="contained"
                  id="i31afg9g"
                >
                  <Iconify icon="eva:download-fill" width={22} height={22} />
                </Button>
              </Tip>

              <Pagination
                data-testid="document-history-pagination"
                count={pageCount()}
                onChange={handleChange}
                disabled={isLoading}
                siblingCount={1}
                boundaryCount={1}
                size="small"
                id="272jj5d2"
              />
            </CardActions>
          </AccordionDetails>
        </Accordion>
      )}
    </Card>
  );
}
